import React, { useContext, useState } from "react";
import styled from "styled-components";
import {
  calculateDeliveryDate,
  workingHoursCutoff,
} from "../../utils/deliveryDate";
import { useTranslation } from "react-i18next";
import {
  qualifiesForFreeShipping,
  transportMethods,
} from "../../utils/global_const";
import { ProductContext } from "../../context/ProductContext";
import { useSelector } from "react-redux";

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xl);
`;

const SectionTitle = styled.h2`
  margin: 0 0 var(--spacing-md) 0;
  padding-bottom: var(--spacing-xs);
  border-bottom: 2px solid var(--text-100);
  font-size: var(--font-size-small);
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--text-100);
`;

const LockedPanel = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-sm);
  background-color: var(--bg-200);
  border: 1px dashed var(--bg-300);
  padding: var(--spacing-md);
`;
const LockedBody = styled.div`
  flex: 1;
  min-width: 0;
`;
const LockIcon = styled.svg`
  flex-shrink: 0;
  margin-top: 2px;
`;
const LockedSectionTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 var(--spacing-xs) 0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-200);
`;

const AddressLabel = styled.p`
  margin: 0 0 4px 0;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-200);
`;

const AddressText = styled.p`
  margin: 0;
  font-size: 15px;
  color: var(--text-200);
`;

const Option = styled.label`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-md);
  padding: var(--spacing-md) 0;
  border-bottom: 1px solid var(--bg-300);
  cursor: pointer;

  &:last-of-type {
    border-bottom: none;
  }
`;

const RadioOuter = styled.span`
  flex-shrink: 0;
  margin-top: 3px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 1px solid
    ${({ $isSelected }) => ($isSelected ? "var(--text-100)" : "var(--bg-300)")};
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: "";
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background-color: var(--text-100);
    transform: scale(${({ $isSelected }) => ($isSelected ? 1 : 0)});
    transition: transform var(--transition-fast);
  }
`;

const OptionBody = styled.div`
  flex: 1;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--spacing-md);
  flex-wrap: wrap;
`;

const OptionName = styled.span`
  display: block;
  font-size: 15px;
  font-weight: 600;
  color: var(--text-100);
`;

const OptionDetails = styled.span`
  display: block;
  margin-top: 4px;
  font-size: var(--font-size-small);
  color: var(--text-200);
`;

const OptionPrice = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: var(--text-100);
  white-space: nowrap;
`;

const CutoffNote = styled.p`
  margin: 6px 0 0 30px;
  font-size: var(--font-size-small);
  color: var(--accent-200);
`;

const Button = styled.button`
  margin-top: var(--spacing-md);
  margin-bottom: var(--spacing-xl);
  width: 100%;
  padding: 15px 20px;
  background-color: transparent;
  color: ${({ disabled }) => (disabled ? "var(--bg-300)" : "var(--text-100)")};
  border: 1px solid ${({ disabled }) => (disabled ? "var(--bg-300)" : "var(--text-100)")};
  border-radius: 0;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  transition: background-color var(--transition-fast), color var(--transition-fast);

  &:hover {
    background-color: ${({ disabled }) => (disabled ? "transparent" : "var(--text-100)")};
    color: ${({ disabled }) => (disabled ? "var(--bg-300)" : "var(--bg-100)")};
  }
`;

const Step2 = ({ onNext, address }) => {
  const { t } = useTranslation();
  const { currencyTag } = useContext(ProductContext);
  const [selectedMethod, setSelectedMethod] = useState(transportMethods[0]);
  const cartItems = useSelector((state) => state.cart.itemsList);
  const subtotalRounded = parseFloat(
    cartItems
      .reduce(
        (total, item) => total + item.product.discount_price * item.quantity,
        0
      )
      .toFixed(2)
  );

  const handleSelect = (method) => {
    setSelectedMethod(method);
  };

  const handleSubmit = () => {
    if (selectedMethod) {
      onNext(selectedMethod);
    }
  };

  return (
    <Container>
      <LockedPanel>
        <LockIcon width="14" height="14" viewBox="0 0 24 24" fill="none">
          <rect
            x="5"
            y="11"
            width="14"
            height="9"
            rx="1.5"
            stroke="var(--text-200)"
            strokeWidth="1.6"
          />
          <path
            d="M8 11V7.5C8 5.01472 9.79086 3 12 3C14.2091 3 16 5.01472 16 7.5V11"
            stroke="var(--text-200)"
            strokeWidth="1.6"
          />
        </LockIcon>
        <LockedBody>
          <LockedSectionTitle>{t("CHECKOUT.DELIVER_TO")}</LockedSectionTitle>
          <AddressLabel>{address.country}</AddressLabel>
          <AddressText>
            {`${address.street} ${address.street_number}, ${address.city} ${address.postal_code}`}
          </AddressText>
        </LockedBody>
      </LockedPanel>

      <div>
        <SectionTitle>{t("CHECKOUT.DELIVERY_OPTIONS")}</SectionTitle>
        {transportMethods.map((method, index) => {
          const isSelected = selectedMethod.name === method.name;
          const price = qualifiesForFreeShipping(subtotalRounded, method.name)
            ? 0
            : method.price;
          return (
            <div key={index}>
              <Option onClick={() => handleSelect(method)}>
                <RadioOuter $isSelected={isSelected} />
                <OptionBody>
                  <div>
                    <OptionName>{method.name}</OptionName>
                    <OptionDetails>
                      {t("CHECKOUT.ESTIMATED_DELIVERY")}{" "}
                      {calculateDeliveryDate(method.days)}
                    </OptionDetails>
                  </div>
                  <OptionPrice>
                    {price === 0
                      ? t("CHECKOUT.FREE")
                      : `${currencyTag}${price.toFixed(2)}`}
                  </OptionPrice>
                </OptionBody>
              </Option>
              {isSelected && (
                <CutoffNote>
                  {t("CHECKOUT.PLACE_ORDER_BEFORE")} {workingHoursCutoff}h{" "}
                  {t("CHECKOUT.SAME_DAY_SHIPPING")}
                </CutoffNote>
              )}
            </div>
          );
        })}

        <Button onClick={handleSubmit} disabled={!selectedMethod}>
          {t("BUTTONS.CONTINUE")}
        </Button>
      </div>
    </Container>
  );
};

export default Step2;
